import { xchacha20poly1305Decrypt, xchacha20poly1305Encrypt } from './aead.js';
import { FRAGMENT_SIZE, parseFragment, serializeFragment, } from './fragment.js';
import { sha256 } from './hash.js';
import { bytesEqual, concatBytes, requireLength } from './internal.js';
export const MANIFEST_VERSION = 1;
export const MANIFEST_ID_SIZE = 32;
export const MANIFEST_NONCE_SIZE = 24;
export const MANIFEST_TAG_SIZE = 16;
const MANIFEST_KEY_SIZE = 32;
const TRANSCRIPT_HASH_SIZE = 32;
const SESSION_ID_SIZE = 32;
const COMMITMENT_SIZE = 32;
const HEADER_SIZE = 4 + MANIFEST_ID_SIZE + MANIFEST_NONCE_SIZE;
const utf8 = new TextEncoder();
const MANIFEST_AD_PREFIX = utf8.encode('fume.manifest.v1');
export class ManifestError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ManifestError';
    }
}
function uint32be(x) {
    assertUint32(x, 'uint32');
    const out = new Uint8Array(4);
    new DataView(out.buffer).setUint32(0, x, false);
    return out;
}
function readUint32be(bytes, offset) {
    if (offset + 4 > bytes.length) {
        throw new ManifestError('manifest body is truncated');
    }
    const view = new DataView(bytes.buffer, bytes.byteOffset + offset, 4);
    return view.getUint32(0, false);
}
function assertUint32(value, name) {
    if (!Number.isInteger(value) || value < 0 || value > 0xffffffff) {
        throw new ManifestError(`${name} must be an integer in 0..2^32-1`);
    }
}
function directionByte(direction) {
    if (direction === 'send')
        return 0x00;
    if (direction === 'recv')
        return 0x01;
    throw new ManifestError(`direction must be 'send' or 'recv'`);
}
function directionFromByte(byte) {
    if (byte === 0x00)
        return 'send';
    if (byte === 0x01)
        return 'recv';
    throw new ManifestError('manifest body has an invalid direction byte');
}
function kindByte(kind) {
    if (kind === 'real')
        return 0x00;
    if (kind === 'chaff')
        return 0x01;
    throw new ManifestError(`entry kind must be 'real' or 'chaff'`);
}
function kindFromByte(byte) {
    if (byte === 0x00)
        return 'real';
    if (byte === 0x01)
        return 'chaff';
    throw new ManifestError('manifest body has an invalid fragment kind byte');
}
function validateBytes(value, expected, name) {
    try {
        requireLength(value, expected, name);
    }
    catch (err) {
        throw new ManifestError(err.message);
    }
}
function manifestAD(sessionId, transcriptHash, version) {
    return concatBytes(MANIFEST_AD_PREFIX, sessionId, transcriptHash, uint32be(version));
}
function sortedEntries(entries) {
    return [...entries].sort((a, b) => a.index - b.index);
}
function sortedAssignments(assignments) {
    return [...assignments].sort((a, b) => a.index - b.index);
}
function assertIndexSet(indices, totalCount, name) {
    if (indices.length !== totalCount) {
        throw new ManifestError(`${name} must contain exactly ${totalCount} records`);
    }
    const seen = new Set();
    for (const index of indices) {
        assertUint32(index, `${name} index`);
        if (index >= totalCount) {
            throw new ManifestError(`${name} index is outside 0..totalCount-1`);
        }
        if (seen.has(index)) {
            throw new ManifestError(`${name} contains duplicate index ${index}`);
        }
        seen.add(index);
    }
    for (let index = 0; index < totalCount; index++) {
        if (!seen.has(index)) {
            throw new ManifestError(`${name} is missing index ${index}`);
        }
    }
}
function validateManifestBodyShape(manifest) {
    if (manifest.version !== MANIFEST_VERSION) {
        throw new ManifestError(`unsupported manifest version ${manifest.version}`);
    }
    directionByte(manifest.direction);
    if (manifest.fragmentSize !== FRAGMENT_SIZE) {
        throw new ManifestError(`fragmentSize must be ${FRAGMENT_SIZE}`);
    }
    assertUint32(manifest.realCount, 'realCount');
    assertUint32(manifest.totalCount, 'totalCount');
    if (manifest.realCount > manifest.totalCount) {
        throw new ManifestError('realCount must be <= totalCount');
    }
    assertIndexSet(manifest.entries.map((entry) => entry.index), manifest.totalCount, 'entries');
    assertIndexSet(manifest.assignments.map((assignment) => assignment.index), manifest.totalCount, 'assignments');
    for (const entry of manifest.entries) {
        kindByte(entry.kind);
        validateBytes(entry.commitment, COMMITMENT_SIZE, 'entry.commitment');
    }
    const commitments = new Set();
    for (const entry of manifest.entries) {
        const commitment = Buffer.from(entry.commitment).toString('hex');
        if (commitments.has(commitment)) {
            throw new ManifestError('entries contain duplicate commitments');
        }
        commitments.add(commitment);
    }
    for (const assignment of manifest.assignments) {
        if (assignment.pipeIds.length === 0) {
            throw new ManifestError('assignment pipeIds must be non-empty');
        }
        for (const pipeId of assignment.pipeIds) {
            if (typeof pipeId !== 'string') {
                throw new ManifestError('pipeId must be a string');
            }
        }
    }
}
function buildBodyFromInputs(fragments, schedule, direction) {
    directionByte(direction);
    const totalCount = fragments.length;
    assertUint32(totalCount, 'totalCount');
    assertIndexSet(fragments.map((fragment) => fragment.index), totalCount, 'fragments');
    let realCount = 0;
    const entries = fragments.map((fragment) => {
        if (fragment.direction !== direction) {
            throw new ManifestError('fragment direction does not match manifest direction');
        }
        if (fragment.totalCount !== totalCount) {
            throw new ManifestError('fragment totalCount does not match fragment array length');
        }
        if (fragment.kind === 'real') {
            realCount += 1;
        }
        else if (fragment.kind !== 'chaff') {
            throw new ManifestError(`fragment kind must be 'real' or 'chaff'`);
        }
        return {
            index: fragment.index,
            kind: fragment.kind,
            commitment: sha256(serializeFragment(fragment)),
        };
    });
    const assignments = [];
    for (let index = 0; index < totalCount; index++) {
        const pipeIds = schedule.assignments.get(index);
        if (pipeIds === undefined || pipeIds.length === 0) {
            throw new ManifestError(`missing or empty pipeIds assignment for index ${index}`);
        }
        assignments.push({ index, pipeIds: [...pipeIds] });
    }
    return {
        version: MANIFEST_VERSION,
        direction,
        fragmentSize: FRAGMENT_SIZE,
        realCount,
        totalCount,
        entries,
        assignments,
    };
}
function nonceFromOptions(options) {
    if (!options?.deterministicNonce)
        return undefined;
    const nonce = options.deterministicNonce();
    validateBytes(nonce, MANIFEST_NONCE_SIZE, 'deterministicNonce()');
    return nonce;
}
function parseCanonicalBody(body, manifestId) {
    if (!(body instanceof Uint8Array)) {
        throw new ManifestError('manifest body must be a Uint8Array');
    }
    let offset = 0;
    const version = readUint32be(body, offset);
    offset += 4;
    if (version !== MANIFEST_VERSION) {
        throw new ManifestError(`unsupported manifest version ${version}`);
    }
    if (offset >= body.length) {
        throw new ManifestError('manifest body is truncated');
    }
    const direction = directionFromByte(body[offset]);
    offset += 1;
    const fragmentSize = readUint32be(body, offset);
    offset += 4;
    const realCount = readUint32be(body, offset);
    offset += 4;
    const totalCount = readUint32be(body, offset);
    offset += 4;
    const entries = [];
    for (let i = 0; i < totalCount; i++) {
        const index = readUint32be(body, offset);
        offset += 4;
        if (offset >= body.length) {
            throw new ManifestError('manifest body is truncated');
        }
        const kind = kindFromByte(body[offset]);
        offset += 1;
        if (offset + COMMITMENT_SIZE > body.length) {
            throw new ManifestError('manifest body is truncated');
        }
        const commitment = body.slice(offset, offset + COMMITMENT_SIZE);
        offset += COMMITMENT_SIZE;
        entries.push({ index, kind, commitment });
    }
    const assignments = [];
    for (let i = 0; i < totalCount; i++) {
        const index = readUint32be(body, offset);
        offset += 4;
        const pipeCount = readUint32be(body, offset);
        offset += 4;
        const pipeIds = [];
        for (let j = 0; j < pipeCount; j++) {
            const byteLength = readUint32be(body, offset);
            offset += 4;
            if (offset + byteLength > body.length) {
                throw new ManifestError('manifest body is truncated');
            }
            pipeIds.push(new TextDecoder().decode(body.subarray(offset, offset + byteLength)));
            offset += byteLength;
        }
        assignments.push({ index, pipeIds });
    }
    if (offset !== body.length) {
        throw new ManifestError('manifest body has trailing bytes');
    }
    const manifest = {
        version,
        direction,
        fragmentSize,
        realCount,
        totalCount,
        entries,
        assignments,
        manifestId,
    };
    validateManifestBodyShape(manifest);
    const recanonicalized = canonicalManifestBody(manifest);
    if (!bytesEqual(recanonicalized, body)) {
        throw new ManifestError('manifest body is not canonical');
    }
    return manifest;
}
export function canonicalManifestBody(manifest) {
    validateManifestBodyShape(manifest);
    const parts = [
        uint32be(manifest.version),
        Uint8Array.of(directionByte(manifest.direction)),
        uint32be(manifest.fragmentSize),
        uint32be(manifest.realCount),
        uint32be(manifest.totalCount),
    ];
    for (const entry of sortedEntries(manifest.entries)) {
        parts.push(uint32be(entry.index), Uint8Array.of(kindByte(entry.kind)), entry.commitment);
    }
    for (const assignment of sortedAssignments(manifest.assignments)) {
        parts.push(uint32be(assignment.index), uint32be(assignment.pipeIds.length));
        for (const pipeId of assignment.pipeIds) {
            const bytes = utf8.encode(pipeId);
            parts.push(uint32be(bytes.length), bytes);
        }
    }
    return concatBytes(...parts);
}
export function computeManifestId(body) {
    if (!(body instanceof Uint8Array)) {
        throw new ManifestError('manifest body must be a Uint8Array');
    }
    return sha256(body);
}
export function buildManifest(fragments, schedule, manifestKey, transcriptHash, sessionId, direction, options) {
    validateBytes(manifestKey, MANIFEST_KEY_SIZE, 'manifestKey');
    validateBytes(transcriptHash, TRANSCRIPT_HASH_SIZE, 'transcriptHash');
    validateBytes(sessionId, SESSION_ID_SIZE, 'sessionId');
    if (!Array.isArray(fragments)) {
        throw new ManifestError('fragments must be an array');
    }
    if (schedule?.assignments === undefined || typeof schedule.assignments.get !== 'function') {
        throw new ManifestError('schedule.assignments must be a ReadonlyMap');
    }
    const manifest = buildBodyFromInputs(fragments, schedule, direction);
    const body = canonicalManifestBody(manifest);
    const manifestId = computeManifestId(body);
    const nonce = nonceFromOptions(options);
    const box = xchacha20poly1305Encrypt(body, manifestAD(sessionId, transcriptHash, MANIFEST_VERSION), manifestKey, nonce);
    return {
        manifestId,
        sealed: concatBytes(uint32be(MANIFEST_VERSION), manifestId, box.nonce, box.ciphertext),
    };
}
export function openManifest(sealed, manifestKey, transcriptHash, sessionId) {
    if (!(sealed instanceof Uint8Array)) {
        throw new ManifestError('sealed manifest must be a Uint8Array');
    }
    validateBytes(manifestKey, MANIFEST_KEY_SIZE, 'manifestKey');
    validateBytes(transcriptHash, TRANSCRIPT_HASH_SIZE, 'transcriptHash');
    validateBytes(sessionId, SESSION_ID_SIZE, 'sessionId');
    if (sealed.length < HEADER_SIZE + MANIFEST_TAG_SIZE) {
        throw new ManifestError('sealed manifest is too short');
    }
    const version = readUint32be(sealed, 0);
    if (version !== MANIFEST_VERSION) {
        throw new ManifestError(`unsupported manifest version ${version}`);
    }
    const headerManifestId = sealed.slice(4, 4 + MANIFEST_ID_SIZE);
    const nonce = sealed.slice(4 + MANIFEST_ID_SIZE, HEADER_SIZE);
    const ciphertext = sealed.slice(HEADER_SIZE);
    const body = xchacha20poly1305Decrypt(ciphertext, manifestAD(sessionId, transcriptHash, version), manifestKey, nonce);
    const computedManifestId = computeManifestId(body);
    if (!bytesEqual(computedManifestId, headerManifestId)) {
        throw new ManifestError('manifestId does not match canonical body');
    }
    return parseCanonicalBody(body, headerManifestId);
}
export function reconcile(manifest, receivedWireBlobs) {
    if (!Array.isArray(receivedWireBlobs)) {
        throw new ManifestError('receivedWireBlobs must be an array');
    }
    validateManifestBodyShape(manifest);
    const entriesByCommitment = new Map();
    for (const entry of manifest.entries) {
        entriesByCommitment.set(Buffer.from(entry.commitment).toString('hex'), entry);
    }
    const matched = new Map();
    const extra = [];
    const duplicates = [];
    for (const wire of receivedWireBlobs) {
        if (!(wire instanceof Uint8Array)) {
            throw new ManifestError('received wire blob must be a Uint8Array');
        }
        const entry = entriesByCommitment.get(Buffer.from(sha256(wire)).toString('hex'));
        if (entry === undefined) {
            extra.push(wire);
            continue;
        }
        if (matched.has(entry.index)) {
            duplicates.push({ wire, index: entry.index });
            continue;
        }
        matched.set(entry.index, parseFragment(wire, {
            kind: entry.kind,
            index: entry.index,
            totalCount: manifest.totalCount,
            direction: manifest.direction,
        }));
    }
    const missing = [];
    for (let index = 0; index < manifest.totalCount; index++) {
        if (!matched.has(index))
            missing.push(index);
    }
    return {
        fragments: [...matched.entries()].sort(([a], [b]) => a - b).map(([, fragment]) => fragment),
        missing,
        extra,
        duplicates,
    };
}
//# sourceMappingURL=manifest.js.map